import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useAccountMenuDropdownPosition } from '../../hooks/useAccountMenuDropdownPosition';

export type BookingAccountMenuProps = {
  onMyBookings: () => void;
  onMyBundles?: () => void;
  onOpenActivity: () => void;
  onOpenProfile: () => void;
  showSignIn: boolean;
  onSignIn: () => void;
  showSignOut: boolean;
  onSignOut?: () => void | Promise<void>;
  /** When set, idioma en este menú en lugar de controles inline en la cabecera. */
  languagePicker?: {
    language: string;
    languages?: readonly string[];
    onLanguageChange: (code: string) => void;
  };
};

/**
 * Menú de cuenta en flujo booking web (paridad con gym): reservas, actividad, perfil, sesión.
 */
const LANG_OPTIONS = [
  { code: 'es', label: 'ES', aria: 'Español' },
  { code: 'en', label: 'EN', aria: 'English' },
  { code: 'fr', label: 'FR', aria: 'Français' },
  { code: 'de', label: 'DE', aria: 'Deutsch' },
  { code: 'pt', label: 'PT', aria: 'Português' },
] as const;

export function BookingAccountMenu({
  onMyBookings,
  onMyBundles,
  onOpenActivity,
  onOpenProfile,
  showSignIn,
  onSignIn,
  showSignOut,
  onSignOut,
  languagePicker,
}: BookingAccountMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const { menuRef, triggerRef } = useAccountMenuDropdownPosition(open);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      // El dropdown se renderiza en un portal (fuera de rootRef), así que hay que excluir también
      // menuRef; si no, clicar un ítem contaría como "fuera" y cerraría antes del onClick.
      const inRoot = rootRef.current?.contains(target);
      const inMenu = menuRef.current?.contains(target);
      if (!inRoot && !inMenu) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const close = useCallback(() => setOpen(false), []);

  const run = useCallback(
    (fn: () => void) => {
      close();
      fn();
    },
    [close],
  );

  return (
    <div className="gym-account-menu" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="gym-account-menu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="gym-account-menu-trigger-icon" aria-hidden>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        </span>
        <span className="gym-account-menu-trigger-chevron" aria-hidden>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </span>
        <span className="sr-only">{t('booking.account_menu_aria')}</span>
      </button>
      {open ? createPortal(
        <div
          ref={menuRef}
          id={menuId}
          className="gym-account-menu-dropdown"
          role="menu"
          aria-orientation="vertical"
        >
          <button type="button" className="gym-account-menu-item" role="menuitem" onClick={() => run(onMyBookings)}>
            {t('booking.my_bookings')}
          </button>
          {onMyBundles ? (
            <button type="button" className="gym-account-menu-item" role="menuitem" onClick={() => run(onMyBundles)}>
              {t('booking.my_bundles', 'Mis bonos')}
            </button>
          ) : null}
          <button type="button" className="gym-account-menu-item" role="menuitem" onClick={() => run(onOpenActivity)}>
            {t('booking.activity_title')}
          </button>
          <button type="button" className="gym-account-menu-item" role="menuitem" onClick={() => run(onOpenProfile)}>
            {t('booking.account_my_profile')}
          </button>
          {languagePicker ? (
            <div className="gym-account-menu-lang-field">
              <label className="gym-account-menu-lang-label" htmlFor={`${menuId}-lang`}>
                {t('booking.language_aria')}
              </label>
              <select
                id={`${menuId}-lang`}
                className="booking-lang-select gym-account-menu-lang-select"
                value={
                  LANG_OPTIONS.some((l) => l.code === languagePicker.language)
                    ? languagePicker.language
                    : LANG_OPTIONS[0]?.code
                }
                onChange={(e) => {
                  languagePicker.onLanguageChange(e.target.value);
                  close();
                }}
              >
                {(languagePicker.languages?.length
                  ? LANG_OPTIONS.filter((l) => languagePicker.languages!.includes(l.code))
                  : [...LANG_OPTIONS]
                ).map(({ code, label, aria }) => (
                  <option key={code} value={code}>
                    {label} — {aria}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          {showSignIn ? (
            <button type="button" className="gym-account-menu-item" role="menuitem" onClick={() => run(onSignIn)}>
              {t('booking.account_sign_in')}
            </button>
          ) : null}
          {showSignOut && onSignOut ? (
            <button
              type="button"
              className="gym-account-menu-item gym-account-menu-item--danger"
              role="menuitem"
              onClick={() => {
                close();
                void onSignOut();
              }}
            >
              {t('gym.sign_out')}
            </button>
          ) : null}
        </div>,
        document.body,
      ) : null}
    </div>
  );
}
